import { Link } from "react-router-dom";
import {
  Database,
  Shield,
  Globe2,
  MessageSquare,
  Lock,
  Zap,
  FileText,
  UploadCloud,
  Search,
  Settings,
  Users,
  CheckCircle2,
  ArrowRight,
  Building2,
  Scale,
  Headset,
  Cpu,
} from "lucide-react";
import { SeoHead } from "../../seo/SeoHead";
import { buildBreadcrumbListJsonLd, buildSoftwareJsonLd } from "../../seo/site";

const SEO_DESCRIPTION =
  "Fonctionnalités Raguia : chatbot RAG, OCR avancé, portail client marque blanche, contrôle d'accès, hébergement France. Découvrez l'assistant documentaire IA B2B.";

const FEATURES = [
  {
    icon: <Database className="size-7 text-[#A43032]" />,
    title: "Chatbot RAG — Réponses sourcées",
    desc: "L'architecture Retrieval-Augmented Generation garantit que chaque réponse est tirée de vos documents. Le chatbot cite ses sources avec le numéro de page, et refuse de répondre si l'information n'est pas disponible — limitant les hallucinations.",
  },
  {
    icon: <FileText className="size-7 text-[#A43032]" />,
    title: "OCR avancé — PDF scannés et images",
    desc: "Pipeline OCR optimisé pour traiter les documents complexes : tableaux, formulaires scannés, images intégrées. Vos archives papier deviennent interrogeables.",
  },
  {
    icon: <UploadCloud className="size-7 text-[#A43032]" />,
    title: "Portail self-service — Glisser-déposer",
    desc: "Interface de gestion intuitive : uploadez vos PDF, Word, Excel par glisser-déposer. Aucune compétence technique requise pour administrer votre base documentaire.",
  },
  {
    icon: <Lock className="size-7 text-[#A43032]" />,
    title: "Contrôle d'accès granulaire",
    desc: "Gestion fine des droits par département et par utilisateur. Chaque collaborateur ne voit que les documents autorisés — RH et comptabilité restent isolés.",
  },
  {
    icon: <MessageSquare className="size-7 text-[#A43032]" />,
    title: "Portail marque blanche",
    desc: "Offrez à vos clients un accès dédié avec vos couleurs et votre logo. Expérience premium sans effort d'intégration technique.",
  },
  {
    icon: <Shield className="size-7 text-[#A43032]" />,
    title: "Sécurité B2B — Isolation stricte",
    desc: "Isolation complète entre locataires (tenants). Chiffrement en transit et au repos, sauvegardes régulières. Vos documents ne sont jamais partagés entre clients.",
  },
  {
    icon: <Globe2 className="size-7 text-[#A43032]" />,
    title: "Souveraineté des données — OVH France",
    desc: "Hébergement exclusif sur les serveurs OVH en France. LLM européens (Mistral AI). Conformité RGPD — vos données restent en Europe.",
  },
  {
    icon: <Settings className="size-7 text-[#A43032]" />,
    title: "Prompts métiers personnalisés",
    desc: "Configuration sectorielle du prompt système : juridique, santé, finance, immobilier, SAV. L'assistant comprend le vocabulaire et les enjeux de votre métier.",
  },
  {
    icon: <Zap className="size-7 text-[#A43032]" />,
    title: "Clé en main — Zéro infrastructure",
    desc: "Infrastructure entièrement gérée : déploiement, mises à jour, sécurité, monitoring. Aucun serveur à maintenir, aucune compétence DevOps requise.",
  },
];

const SECTORS = [
  {
    icon: <Users className="size-6 text-[#A43032]" />,
    title: "Cabinets RH",
    desc: "Règlements intérieurs, fiches de poste, conventions collectives.",
  },
  {
    icon: <Building2 className="size-6 text-[#A43032]" />,
    title: "Immobilier",
    desc: "Mandats, compromis, diagnostics, baux de location.",
  },
  {
    icon: <Scale className="size-6 text-[#A43032]" />,
    title: "Légal & Compta",
    desc: "Procédures internes, normes juridiques, bilans.",
  },
  {
    icon: <Headset className="size-6 text-[#A43032]" />,
    title: "SAV / Support",
    desc: "Documentation produit, FAQ interne, manuels techniques.",
  },
];

export default function FonctionnalitesPage() {
  const jsonLd = [
    buildBreadcrumbListJsonLd([
      { name: "Accueil", path: "/" },
      { name: "Fonctionnalités", path: "/fonctionnalites" },
    ]),
    buildSoftwareJsonLd(),
  ];

  return (
    <>
      <SeoHead
        title="Fonctionnalités — Raguia"
        description={SEO_DESCRIPTION}
        jsonLd={jsonLd}
      />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        <p className="text-sm font-semibold uppercase tracking-wide text-[#A43032]">
          Fonctionnalités
        </p>
        <h1 className="font-heading mt-2 text-4xl font-semibold tracking-tight text-[#010101] sm:text-5xl">
          Tout ce que Raguia fait pour vos documents
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-relaxed text-stone-800">
          Chatbot RAG, OCR, portail client, contrôle d'accès — une plateforme
          complète pour transformer vos documents internes en réponses sourcées,
          hébergée en France.
        </p>

        <section className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <article
              key={f.title}
              className="rounded-2xl border-2 border-[#010101]/10 bg-white p-6 shadow-sm transition hover:border-[#A43032]/30 hover:shadow-md"
            >
              <div className="size-12 rounded-xl bg-stone-100 flex items-center justify-center mb-4">
                {f.icon}
              </div>
              <h2 className="font-heading text-lg font-semibold text-[#010101] mb-2">
                {f.title}
              </h2>
              <p className="text-sm leading-relaxed text-stone-700">{f.desc}</p>
            </article>
          ))}
        </section>

        <section className="mt-20">
          <h2 className="font-heading text-2xl font-semibold text-[#010101] mb-8">
            Adapté à votre secteur
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SECTORS.map((s) => (
              <div
                key={s.title}
                className="rounded-2xl border-2 border-[#010101]/10 bg-white p-5 flex flex-col items-center text-center shadow-sm hover:border-[#A43032]/30 transition"
              >
                <div className="size-10 rounded-lg bg-stone-100 flex items-center justify-center mb-3">
                  {s.icon}
                </div>
                <h3 className="font-heading font-semibold text-[#010101] mb-1">{s.title}</h3>
                <p className="text-xs text-stone-600 font-medium">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-20 rounded-2xl bg-[#010101] p-8 md:p-12 text-center relative overflow-hidden border border-[#A43032]/20 shadow-2xl">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(164,48,50,0.3),transparent_70%)]" />
          <div className="relative z-10 flex flex-col items-center">
            <Cpu className="size-14 text-[#A43032] mb-5" />
            <h2 className="font-heading text-2xl md:text-4xl font-black text-white mb-4 tracking-tight">
              Prêt à essayer sur vos documents ?
            </h2>
            <p className="text-base md:text-lg text-stone-300 max-w-2xl mb-8 font-medium">
              Demandez une démo gratuite et voyez Raguia en action sur vos
              propres données.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-2xl bg-[#A43032] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#A43032]/20 transition-transform hover:scale-105"
            >
              Demander une démo <ArrowRight className="size-5" />
            </Link>
            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-stone-400 font-bold">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-[#A43032]" /> Démo
                gratuite
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="size-4 text-[#A43032]" /> Sans
                engagement
              </span>
            </div>
          </div>
        </section>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-[#A43032] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-[#8c282a]"
          >
            Nous contacter <ArrowRight className="size-4" />
          </Link>
          <Link
            to="/"
            className="inline-flex rounded-full border-2 border-[#010101] bg-white px-6 py-3 text-sm font-semibold text-[#010101] transition hover:bg-[#010101] hover:text-white"
          >
            Retour accueil
          </Link>
        </div>
      </main>
    </>
  );
}
